export const validateVideoDuration = (file, maxSeconds = 15) => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve(video.duration <= maxSeconds);
    };
    video.onerror = () => resolve(false);
    video.src = URL.createObjectURL(file);
  });
};

// Helper: generate a poster thumbnail from a video file (returns a Blob)
export const generateVideoThumbnail = (videoFile) => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    const url = URL.createObjectURL(videoFile);
    video.src = url;
    video.onloadeddata = () => {
      video.currentTime = Math.min(0.5, video.duration / 2);
    };
    video.onseeked = () => {
      const canvas = document.createElement('canvas');
      canvas.width = Math.min(video.videoWidth, 480);
      canvas.height = Math.round(canvas.width * (video.videoHeight / video.videoWidth));
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        URL.revokeObjectURL(url);
        resolve(blob);
      }, 'image/jpeg', 0.7);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
  });
};

// Helper: compress video client-side (re-encode at lower bitrate)
export const compressVideo = (videoFile, targetBitrate = 1500000) => {
  return new Promise((resolve) => {
    // If MediaRecorder doesn't support VP8/webm or file is already small, skip compression
    if (!window.MediaRecorder || !MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')) {
      resolve(videoFile);
      return;
    }
    // Skip if already under 5MB
    if (videoFile.size < 5 * 1024 * 1024) {
      resolve(videoFile);
      return;
    }

    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    const url = URL.createObjectURL(videoFile);
    video.src = url;

    video.onloadedmetadata = () => {
      const canvas = document.createElement('canvas');
      // Cap resolution at 720p
      const scale = video.videoWidth > 1280 ? 1280 / video.videoWidth : 1;
      canvas.width = Math.round(video.videoWidth * scale);
      canvas.height = Math.round(video.videoHeight * scale);
      const ctx = canvas.getContext('2d');

      const stream = canvas.captureStream(30);
      // Add audio track if present
      try {
        const audioCtx = new AudioContext();
        const source = audioCtx.createMediaElementSource(video);
        const dest = audioCtx.createMediaStreamDestination();
        source.connect(dest);
        source.connect(audioCtx.destination);
        dest.stream.getAudioTracks().forEach(t => stream.addTrack(t));
      } catch (e) { /* no audio track, that's fine */ }

      const recorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp8,opus',
        videoBitsPerSecond: targetBitrate,
      });

      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = () => {
        URL.revokeObjectURL(url);
        const compressed = new Blob(chunks, { type: 'video/webm' });
        // Only use compressed if it's actually smaller
        if (compressed.size < videoFile.size) {
          const compressedFile = new File([compressed], videoFile.name.replace(/\.[^.]+$/, '.webm'), { type: 'video/webm' });
          resolve(compressedFile);
        } else {
          resolve(videoFile);
        }
      };

      video.play();
      recorder.start();

      const drawFrame = () => {
        if (video.ended || video.paused) {
          recorder.stop();
          return;
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        requestAnimationFrame(drawFrame);
      };
      drawFrame();

      video.onended = () => { recorder.stop(); };

      // Safety timeout: if video is longer than 2 minutes, just use original
      setTimeout(() => {
        if (recorder.state === 'recording') {
          video.pause();
          recorder.stop();
        }
      }, Math.min(video.duration * 1000 + 2000, 120000));
    };

    video.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(videoFile);
    };
  });
};

// Re-encode video through canvas to bake in rotation metadata (fixes Edge sideways videos)
export const fixVideoOrientation = (videoFile) => {
  return new Promise((resolve) => {
    if (!window.MediaRecorder || !MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')) {
      resolve(videoFile);
      return;
    }
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    const url = URL.createObjectURL(videoFile);
    video.src = url;

    video.onloadedmetadata = () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');

      const stream = canvas.captureStream(30);
      try {
        const audioCtx = new AudioContext();
        const source = audioCtx.createMediaElementSource(video);
        const dest = audioCtx.createMediaStreamDestination();
        source.connect(dest);
        source.connect(audioCtx.destination);
        dest.stream.getAudioTracks().forEach(t => stream.addTrack(t));
      } catch (e) { /* no audio */ }

      const recorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp8,opus',
        videoBitsPerSecond: 2000000,
      });

      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = () => {
        URL.revokeObjectURL(url);
        const blob = new Blob(chunks, { type: 'video/webm' });
        const fixed = new File([blob], videoFile.name.replace(/\.[^.]+$/, '.webm'), { type: 'video/webm' });
        resolve(fixed);
      };

      video.play();
      recorder.start();

      const drawFrame = () => {
        if (video.ended || video.paused) { recorder.stop(); return; }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        requestAnimationFrame(drawFrame);
      };
      drawFrame();
      video.onended = () => { recorder.stop(); };

      // Safety timeout for 15s chat videos
      setTimeout(() => {
        if (recorder.state === 'recording') { video.pause(); recorder.stop(); }
      }, Math.min((video.duration || 15) * 1000 + 2000, 20000));
    };

    video.onerror = () => { URL.revokeObjectURL(url); resolve(videoFile); };
  });
};
